function finitePositive(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}
function clampToCapability(value, capability) {
    const requested = finitePositive(value);
    if (!requested)
        return 0;
    const minimum = finitePositive(capability?.min);
    const maximum = finitePositive(capability?.max);
    return Math.max(minimum || requested, Math.min(maximum || requested, requested));
}
export function preferredCameraFormat(preferred, capabilities = {}) {
    return {
        width: Math.round(clampToCapability(preferred?.width, capabilities.width)),
        height: Math.round(clampToCapability(preferred?.height, capabilities.height)),
        fps: clampToCapability(preferred?.fps, capabilities.frameRate),
    };
}
function exactConstraints(format) {
    const constraints = {};
    if (format.width)
        constraints.width = { exact: format.width };
    if (format.height)
        constraints.height = { exact: format.height };
    if (format.fps)
        constraints.frameRate = { exact: format.fps };
    return constraints;
}
function boundedConstraints(format) {
    const constraints = {};
    if (format.width)
        constraints.width = { ideal: format.width };
    if (format.height)
        constraints.height = { ideal: format.height };
    if (format.fps)
        constraints.frameRate = { ideal: format.fps, max: format.fps };
    return constraints;
}
/**
 * Re-negotiate an already-open camera track against the device capabilities.
 *
 * Browsers expose camera labels before they expose capabilities. Opening the
 * device first, then applying an exact supported mode prevents a soft 60 fps
 * request from silently choosing a low-bandwidth raw format (for example,
 * 1280x720 YUY2 at 10 fps instead of MJPEG at 30 fps).
 */
export async function negotiateCameraTrack(track, preferred) {
    const capabilities = track?.getCapabilities?.() || {};
    const requested = {
        width: Math.round(finitePositive(preferred?.width)),
        height: Math.round(finitePositive(preferred?.height)),
        fps: finitePositive(preferred?.fps),
    };
    const target = preferredCameraFormat(requested, capabilities);
    let strategy = "initial";
    let exactError = null;
    if (track?.applyConstraints && (target.width || target.height || target.fps)) {
        try {
            await track.applyConstraints(exactConstraints(target));
            strategy = "exact";
        }
        catch (error) {
            exactError = error instanceof Error ? error.message : String(error);
            await track.applyConstraints(boundedConstraints(target));
            strategy = "bounded";
        }
    }
    return {
        requested,
        target,
        negotiated: track?.getSettings?.() || {},
        capabilities,
        strategy,
        exactError,
    };
}
//# sourceMappingURL=cameraFormat.js.map